import { type FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { copy } from '../../config/copy';
import { useSession } from '../../stores/session';
import { AuthLayout } from './AuthLayout';
import { providerError } from './providers';
import { ProviderSignIn } from './ProviderSignIn';
import { useFormErrors } from './useFormErrors';
import { useDocumentTitle } from '../../lib/title';

export function LoginPage() {
  useDocumentTitle('Sign in');

  const login = useSession((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const errors = useFormErrors();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  // Where RequireAuth sent them from, so a deep link survives signing in.
  const destination = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  /*
   * Something the Discord callback decided, on its way back. It can only speak
   * in redirects, so this is how it says what went wrong - see `providerError`.
   */
  const returned = providerError(new URLSearchParams(location.search).get('error'));

  async function submit(event: FormEvent) {
    event.preventDefault();
    errors.clear();
    setBusy(true);

    try {
      await login(email, password);
      navigate(destination, { replace: true });
    } catch (error) {
      errors.capture(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      stage={{
        headline: 'The room kept going.',
        pitch:
          'Hosts have been on air, queues have moved, and your face is where you left it. Pick up wherever the room got to.',
      }}
      footer={
        <>
          No account? <Link to="/register" className="text-accent hover:underline">Create one</Link>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/*
          Above the form, because it is one click against four fields and
          putting the shorter path second makes people fill in the longer one
          before they notice it.
        */}
        <ProviderSignIn next={destination} />

        {returned !== null && <Alert tone="error">{returned}</Alert>}
      </div>

      <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
        {errors.message && <Alert tone="error">{errors.message}</Alert>}

        <Field
          label="Email"
          type="email"
          name="email"
          // The browser needs these to offer a saved password, and a login form
          // that fights the password manager is a login form people reuse a
          // weak password on.
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.fields.email}
        />

        <Field
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.fields.password}
        />

        <Button type="submit" loading={busy} full>
          {copy['cta.login']}
        </Button>

        <Link to="/forgot-password" className="text-center text-sm text-soft hover:text-ink">
          Forgot your password?
        </Link>
      </form>
    </AuthLayout>
  );
}
